import { useCallback, useContext, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { InvalidBasketStatusModal } from "@/components/Portal/AddDevice/InvalidBasketStatusModal/InvalidBasketStatusModal";
import { DataContext } from "@/provider/DataContextProvider";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { useInvalidateBasket } from "@/services/api";
import { isInvalidBasketStatusError } from "@/utils/basketErrorUtils";

/**
 * Detects Compax "invalid basket status" errors, shows an info dialog, then
 * invalidates the basket (same endpoint as the Add Device overflow menu) and
 * restarts the activate-SIM flow so POST /baskets can create a fresh basket.
 */
export const useInvalidBasketRecovery = () => {
  const { dataContext, setDataContext } = useContext(DataContext);
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { mutateAsync: invalidateBasket, isLoading: isRecovering } =
    useInvalidateBasket();
  const recoveryStartedRef = useRef(false);

  // For other errors that leave the basket unusable.
  const openRestartDialog = useCallback((): boolean => {
    if (recoveryStartedRef.current) {
      return false;
    }

    setIsModalOpen(true);

    return true;
  }, []);

  const openIfInvalidBasketError = useCallback(
    (error: unknown): boolean =>
      isInvalidBasketStatusError(error) && openRestartDialog(),
    [openRestartDialog],
  );

  const recover = useCallback(async () => {
    if (recoveryStartedRef.current) {
      return;
    }

    recoveryStartedRef.current = true;
    const basketId = dataContext.basket?.id;

    try {
      if (basketId !== undefined) {
        await invalidateBasket({ basketId });
      }
    } catch (error) {
      console.error("Failed to invalidate basket with invalid status:", error);
      // Still clear local state — Compax may already have closed the basket.
    }

    setDataContext((prev) => ({
      allowedMaxOnboardingStep: 1,
      isEmailVerified: prev.isEmailVerified,
    }));
    setIsModalOpen(false);
    navigate(portalRoutes.addDevice.simSelection, { replace: true });
  }, [dataContext.basket?.id, invalidateBasket, navigate, setDataContext]);

  const invalidBasketModal = isModalOpen ? (
    <InvalidBasketStatusModal onConfirm={recover} isConfirming={isRecovering} />
  ) : null;

  return {
    openIfInvalidBasketError,
    openRestartDialog,
    invalidBasketModal,
    isInvalidBasketModalOpen: isModalOpen,
  };
};
